import { ProxyAction } from "../Custom Hooks/useProxyAction";
import { ReactNode, useCallback, useState } from "react";
import { Flex, Stack, StackDirection, useBreakpointValue, VStack } from "@chakra-ui/react";
import Title from "../Common/Title";
import { Typography } from "@sgi/gravity/Typography";
import { ErrorMessage } from "@sgi/gravity/ErrorMessage";
import { FAIcon, Radio, RadioGroup } from "@sgi/gravity";
import { faMessageLines, faPhone } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@sgi/gravity/Button";
import CancelButton from "../Common/Form/CancelButton";

export type PhoneFactorMethodSelectionProps = {
  readonly pageError: string;
  readonly setSelectedValue: (value: string) => void;
  readonly lastTwoDigits: string;
  readonly sendCodeAction: ProxyAction;
  readonly callMeAction: ProxyAction;
  readonly verificationCodeError: string;
  readonly children?: ReactNode;
}

export default function PhoneFactorMethodSelection({
  pageError,
  setSelectedValue,
  lastTwoDigits,
  sendCodeAction,
  callMeAction,
  verificationCodeError,
  children,
}: PhoneFactorMethodSelectionProps) {
  const [method, setMethod] = useState('text');
  const direction = useBreakpointValue({base: "column", md: "row"}) as StackDirection;
  const width = useBreakpointValue({base: "100%", md: "auto"});

  const handleRadioChange = (value: string | boolean) => {
    if (typeof value === 'string') {
      setMethod(value);
    }
  };

  const handleContinueButtonClick = useCallback(() => {
    if (method === 'text') {
      sendCodeAction.act();
    } else if (method === 'call') {
      callMeAction.act();
    }
    setSelectedValue(method);
  }, [setSelectedValue, sendCodeAction, callMeAction, method]);

  return (
    <Flex
      direction="column"
      maxW={544}
      padding={38}
      mx="auto"
      justifyContent="center"
      data-testid="phone-factor-choose-method"
    >
      <Title text="Verify your account"/>
      <Typography>
        Multi-factor authentication is set up for your account. Choose how you'd like to verify your account.
      </Typography>
      <Flex
        direction="column"
        maxW={600}
        padding={30}
        mx="auto"
        data-testid="confirm-phone-factor-test"
      >
        {pageError && <ErrorMessage>{pageError}</ErrorMessage>}
        {verificationCodeError && <ErrorMessage>{verificationCodeError}</ErrorMessage>}
        <RadioGroup
          value={method}
          name="method"
          label="Choose a method"
          variant="card"
          isRequired
          controlStyleProps={{mt: 4}}
          onChange={handleRadioChange}
          data-testid="choose-a-method"
        >
          <Stack direction={direction} data-testid="method-stack-test">
            <Radio value="text" flex={1} width={width} data-testid="text-me-radio-test" className="custom-radio">
              <VStack alignItems="center">
                <FAIcon icon={faMessageLines}/>
                <span>Text Me</span>
                <Typography variant="bodyBold">+1 (•••) ••• - ••{lastTwoDigits}</Typography>
              </VStack>
            </Radio>
            <Radio value="call" flex={1} width={width} data-testid="call-me-radio-test" className="custom-radio">
              <VStack alignItems="center">
                <FAIcon icon={faPhone}/>
                <span>Call Me</span>
                <Typography variant="bodyBold">+1 (•••) ••• - ••{lastTwoDigits}</Typography>
              </VStack>
            </Radio>
          </Stack>
        </RadioGroup>
        <Button
          isFullWidth
          name="continue"
          id="continue"
          data-testid="continue-button-test"
          onClick={handleContinueButtonClick}
          mt={2}
        >
          Continue
        </Button>
        <CancelButton data-testid="cancel-button-test" my={1} mt={2} />
        {children}
      </Flex>
    </Flex>
  );
}
